import { useAuth0 } from '@auth0/auth0-react';
import { LicenseInfo } from '@mui/x-license';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { createContextFactory } from '@tempo-io/feature-flag-context';
import { setFroalaLicenseKey } from '@tempo-io/fim3-sui-shared';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { FeatureFlagDevTools } from '@/common/components/dev-tools/FeatureFlagDevTools';
import { GlobalErrorBoundary } from '@/common/components/error/GlobalErrorBoundary';
import { FullSizeSpinner } from '@/common/components/ui/FullSizeSpinner';
import { useFlagOverrides } from '@/common/hooks/feature-flags/useFlagsWithOverrides';
import { useDatadogUserTracking } from '@/common/hooks/monitoring/useDatadogUserTracking';
import { useLaunchDarklyDatadogIntegration } from '@/common/hooks/monitoring/useLaunchDarklyDatadogIntegration';
import { AuthProvider } from '@/common/providers/auth-provider/authProvider';
// biome-ignore lint/style/noRestrictedImports: the QueryClientProvider needs the singleton; all other code must use useQueryClient()
import { queryClient } from '@/common/queryClient';
import { TRPCProvider } from '@/common/trpc/trpc';
import { getEnv } from '@/common/utils/getEnv';
import { router } from './router';

export const ldContextFactory = createContextFactory({ namespace: getEnv().namespace });

function RouterProviderWithContext() {
  const auth = useAuth0();
  const flagOverrides = useFlagOverrides();
  const ldClient = useLDClient();
  useDatadogUserTracking();
  useLaunchDarklyDatadogIntegration();

  if (!ldClient) {
    return (
      <div className="h-screen w-screen">
        <FullSizeSpinner />
      </div>
    );
  }

  return <RouterProvider router={router} context={{ auth, flagOverrides, ldClient }} />;
}

function App() {
  const env = getEnv();
  LicenseInfo.setLicenseKey(env.muiProLicenseId || '');
  setFroalaLicenseKey(env.froalaLicenseId || '');
  return (
    <GlobalErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TRPCProvider>
            <RouterProviderWithContext />
            <FeatureFlagDevTools />
          </TRPCProvider>
        </AuthProvider>
      </QueryClientProvider>
    </GlobalErrorBoundary>
  );
}

export { App };
